import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useFetchCategorias } from "../../../produtos/hooks/useFetchCategorias";
import { ICampanha, IItemCampanha, TipoAlvoPrecoCampanha } from "../../interfaces/ICampanha";
import { LinhaPreco, chaveItem, formatarInputData, montarLinhasPreco } from "../../utils/campanha";

interface NoFolha {
    type: "folha";
    linha: LinhaPreco;
}

interface NoGrupo {
    type: "grupo";
    label: string;
    filhos: NoFolha[];
}

interface NoProduto {
    type: "produto";
    produtoUuid: string;
    produtoDescricao: string;
    filhos: (NoFolha | NoGrupo)[];
}

interface NoCategoria {
    type: "categoria";
    categoriaUuid: string;
    categoriaDescricao: string;
    produtos: NoProduto[];
}

interface Props {
    open: boolean;
    campanha: ICampanha | null;
    onClose: () => void;
    onSalvar: (payload: ICampanha) => Promise<void>;
    loading: boolean;
}

export const CampanhaFormDrawer = ({ open, campanha, onClose, onSalvar, loading }: Props) => {
    const snackbar = useSnackbar();
    const { real, number } = useMoeda();
    const { categorias } = useFetchCategorias();

    const [nomeForm, setNomeForm] = useState("");
    const [descricaoForm, setDescricaoForm] = useState("");
    const [inicioForm, setInicioForm] = useState("");
    const [terminoForm, setTerminoForm] = useState("");
    const [itensSelecionados, setItensSelecionados] = useState<Record<string, IItemCampanha>>({});
    const [precosPromocionais, setPrecosPromocionais] = useState<Record<string, string>>({});
    const [expandedLeafKey, setExpandedLeafKey] = useState<string | null>(null);
    const [precoInputTemp, setPrecoInputTemp] = useState<Record<string, string>>({});

    useEffect(() => {
        if(!open) return;
        setNomeForm(campanha?.nome ?? "");
        setDescricaoForm(campanha?.descricao ?? "");
        setInicioForm(formatarInputData(campanha?.inicioAs));
        setTerminoForm(formatarInputData(campanha?.terminoAs));
        const selecionados: Record<string, IItemCampanha> = {};
        const precos: Record<string, string> = {};
        campanha?.itens?.forEach(item => {
            const key = chaveItem(item);
            selecionados[key] = item;
            precos[key] = String(item.precoPromocional ?? "");
        });
        setItensSelecionados(selecionados);
        setPrecosPromocionais(precos);
        setExpandedLeafKey(null);
        setPrecoInputTemp({});
    }, [open, campanha]);

    const linhasPreco = useMemo(() => montarLinhasPreco(categorias ?? []), [categorias]);

    const arvore = useMemo((): NoCategoria[] => {
        const linhasByKey = new Map(linhasPreco.map(l => [l.key, l]));

        const criarGrupo = (
            label: string,
            itens: any[],
            tipoAlvo: TipoAlvoPrecoCampanha,
            catUuid: string,
            prodUuid: string,
            complementoUuid?: string
        ): NoGrupo | null => {
            const grupo: NoGrupo = { type: "grupo", label, filhos: [] };
            (itens ?? []).forEach(item => {
                const key = `${catUuid}:${prodUuid}:${tipoAlvo}:${item.uuid}:${complementoUuid ?? ""}`;
                const linha = linhasByKey.get(key);
                if(linha) grupo.filhos.push({ type: "folha", linha });
            });
            return grupo.filhos.length > 0 ? grupo : null;
        };

        return (categorias ?? []).map(categoria => {
            const produtos: NoProduto[] = (categoria.produtos ?? []).map(produto => {
                const filhos: (NoFolha | NoGrupo)[] = [];

                // Produto com preço próprio
                const keyProduto = `${categoria.uuid}:${produto.uuid}:PRODUTO:PRODUTO:`;
                const linhaProduto = linhasByKey.get(keyProduto);
                if(linhaProduto) filhos.push({ type: "folha", linha: linhaProduto });

                // Variantes simples
                const variacoes: [string, any[] | undefined, TipoAlvoPrecoCampanha][] = [
                    ["Massas", produto.massas, "MASSA"],
                    ["Bordas", produto.bordas, "BORDA"],
                    ["Sabores", produto.sabores, "SABOR"],
                    ["Adicionais", produto.adicionais, "ADICIONAL"],
                ];
                variacoes.forEach(([label, itens, tipo]) => {
                    const grupo = criarGrupo(label, itens ?? [], tipo, categoria.uuid ?? "", produto.uuid ?? "");
                    if(grupo) filhos.push(grupo);
                });

                // Complementos (cada complemento vira um grupo com seu próprio nome)
                (produto.complementos ?? []).forEach((complemento: any) => {
                    const grupo = criarGrupo(complemento.descricao, complemento.itens, "ITEM_COMPLEMENTO", categoria.uuid ?? "", produto.uuid ?? "", complemento.uuid);
                    if(grupo) filhos.push(grupo);
                });

                // Combo (reaproveitando linhasPreco — a recursão já resolveu os itens)
                const comboLinhas = linhasPreco.filter(
                    l => l.categoriaUuid === categoria.uuid && l.produtoUuid === produto.uuid && l.tipoAlvo === "ITEM_COMBO"
                );
                if(comboLinhas.length) {
                    filhos.push({ type: "grupo", label: "Combo", filhos: comboLinhas.map(l => ({ type: "folha", linha: l })) });
                }

                return { type: "produto" as const, produtoUuid: produto.uuid ?? "", produtoDescricao: produto.descricao, filhos };
            }).filter(p => p.filhos.length > 0);

            return { type: "categoria" as const, categoriaUuid: categoria.uuid ?? "", categoriaDescricao: categoria.descricao, produtos };
        }).filter(c => c.produtos.length > 0);
    }, [categorias, linhasPreco]);

    const adicionarItem = (linha: LinhaPreco) => {
        const precoStr = precoInputTemp[linha.key] ?? "";
        const preco = number(precoStr);
        if(!preco || preco <= 0) {
            return snackbar("Informe um preço promocional válido", { severity: "error" });
        }
        if(preco >= linha.precoOriginal) {
            return snackbar("O preço promocional deve ser menor que o preço atual", { severity: "error" });
        }
        setItensSelecionados(prev => ({
            ...prev,
            [linha.key]: {
                categoriaUuid: linha.categoriaUuid,
                produtoUuid: linha.produtoUuid,
                tipoAlvo: linha.tipoAlvo,
                alvoUuid: linha.alvoUuid,
                complementoUuid: linha.complementoUuid,
                descricao: linha.descricao,
                caminho: linha.caminho,
                precoOriginal: linha.precoOriginal,
                precoPromocional: preco,
                precoSemDescontoOriginal: linha.precoSemDescontoOriginal ?? null
            }
        }));
        setPrecosPromocionais(prev => ({ ...prev, [linha.key]: precoStr }));
        setExpandedLeafKey(null);
    };

    const removerItem = (key: string) => {
        setItensSelecionados(prev => { const n = { ...prev }; delete n[key]; return n; });
        setPrecosPromocionais(prev => { const n = { ...prev }; delete n[key]; return n; });
        setPrecoInputTemp(prev => { const n = { ...prev }; delete n[key]; return n; });
    };

    const toggleLeaf = (key: string) => {
        setExpandedLeafKey(prev => (prev === key ? null : key));
    };

    const handleSalvar = async () => {
        if(!nomeForm.trim()) {
            return snackbar("Informe o nome da campanha", { severity: "error" });
        }
        const itens = Object.entries(itensSelecionados).map(([key, item]) => ({
            ...item,
            precoPromocional: number(precosPromocionais[key])
        }));
        if(!itens.length) {
            return snackbar("Selecione pelo menos um preço", { severity: "error" });
        }
        if(itens.some(item => item.precoPromocional <= 0 || item.precoPromocional >= item.precoOriginal)) {
            return snackbar("Informe preços promocionais menores que os preços atuais", { severity: "error" });
        }
        await onSalvar({
            nome: nomeForm.trim(),
            descricao: descricaoForm.trim(),
            inicioAs: inicioForm || null,
            terminoAs: terminoForm || null,
            itens
        });
    };

    const renderFolha = (folha: NoFolha, indentLevel = 0) => {
        const { linha } = folha;
        const jaSelecionado = Boolean(itensSelecionados[linha.key]);
        const expandido = expandedLeafKey === linha.key;

        return (
            <Box key={linha.key}>
                <Box
                    onClick={() => !jaSelecionado && toggleLeaf(linha.key)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 2,
                        py: 0.75,
                        pl: 2 + indentLevel * 2,
                        cursor: jaSelecionado ? "default" : "pointer",
                        bgcolor: jaSelecionado ? "#e8f5e9" : expandido ? "action.selected" : "inherit",
                        "&:hover": { bgcolor: jaSelecionado ? "#e8f5e9" : "action.hover" },
                        borderBottom: "1px solid",
                        borderColor: "divider"
                    }}
                >
                    {jaSelecionado ? (
                        <CheckCircleIcon sx={{ color: "success.dark", mr: 1, fontSize: 18 }} />
                    ) : (
                        <AddIcon sx={{ color: "text.secondary", mr: 1, fontSize: 18 }} />
                    )}
                    <Typography variant="body2" sx={{ flex: 1 }}>
                        {linha.descricao}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        {real(linha.precoOriginal)}
                    </Typography>
                    {jaSelecionado && (
                        <Chip label="Adicionado" size="small" color="success" variant="outlined" />
                    )}
                </Box>

                <Collapse in={expandido && !jaSelecionado} unmountOnExit>
                    <Box sx={{ px: 3, py: 1.5, bgcolor: "action.hover", borderBottom: "1px solid", borderColor: "divider" }}>
                        <Typography variant="caption" color="text.secondary">
                            Preço atual: {real(linha.precoOriginal)}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
                            <TextField
                                size="small"
                                label="Preço promocional"
                                value={precoInputTemp[linha.key] ?? ""}
                                onChange={e => setPrecoInputTemp(prev => ({ ...prev, [linha.key]: e.target.value }))}
                                placeholder="0,00"
                                sx={{ width: 180 }}
                                autoFocus
                            />
                            <Button variant="contained" size="small" onClick={() => adicionarItem(linha)}>
                                Adicionar
                            </Button>
                            <Button size="small" onClick={() => setExpandedLeafKey(null)}>
                                Cancelar
                            </Button>
                        </Box>
                    </Box>
                </Collapse>
            </Box>
        );
    };

    const renderProduto = (produto: NoProduto) => {
        const folhaProduto = produto.filhos.find((f): f is NoFolha => f.type === "folha" && f.linha.tipoAlvo === "PRODUTO");
        const grupos = produto.filhos.filter((f): f is NoGrupo => f.type === "grupo");

        return (
            <Box key={produto.produtoUuid} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
                <Box sx={{ px: 2, py: 1, bgcolor: "grey.100" }}>
                    <Typography variant="body2" fontWeight="medium">
                        {produto.produtoDescricao}
                    </Typography>
                </Box>

                {folhaProduto && renderFolha(folhaProduto, 0)}

                {grupos.map(grupo => (
                    <Box key={grupo.label}>
                        <Box sx={{ px: 3, py: 0.5, bgcolor: "grey.50" }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: "uppercase" }}>
                                {grupo.label}
                            </Typography>
                        </Box>
                        {grupo.filhos.map(f => renderFolha(f, 2))}
                    </Box>
                ))}
            </Box>
        );
    };

    const qtdSelecionados = Object.keys(itensSelecionados).length;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: "100vw", md: "90vw" }, maxWidth: 1400, display: "flex", flexDirection: "column" } }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, flexShrink: 0 }}>
                <Typography variant="h6">
                    {campanha ? "Editar campanha" : "Nova campanha"}
                </Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Campos do formulário */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: 2, p: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
                <TextField label="Nome" value={nomeForm} onChange={e => setNomeForm(e.target.value)} size="small" />
                <TextField label="Descrição" value={descricaoForm} onChange={e => setDescricaoForm(e.target.value)} size="small" />
                <TextField label="Início" type="datetime-local" value={inicioForm} onChange={e => setInicioForm(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                <TextField label="Término" type="datetime-local" value={terminoForm} onChange={e => setTerminoForm(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
            </Box>

            {/* Dois painéis */}
            <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
                {/* Painel esquerdo: itens selecionados */}
                <Box sx={{ width: "40%", borderRight: 1, borderColor: "divider", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            Itens selecionados ({qtdSelecionados})
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: "auto" }}>
                        {qtdSelecionados === 0 ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 3 }}>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    Selecione itens no cardápio ao lado para adicionar à campanha
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {Object.entries(itensSelecionados).map(([key, item]) => (
                                    <ListItem
                                        key={key}
                                        divider
                                        secondaryAction={
                                            <IconButton edge="end" size="small" onClick={() => removerItem(key)}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        }
                                        sx={{ pr: 6 }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" noWrap title={item.caminho}>
                                                    {item.caminho}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        De {real(item.precoOriginal)} por:
                                                    </Typography>
                                                    <TextField
                                                        size="small"
                                                        value={precosPromocionais[key] ?? ""}
                                                        onChange={e => setPrecosPromocionais(prev => ({ ...prev, [key]: e.target.value }))}
                                                        placeholder="0,00"
                                                        sx={{ width: 100 }}
                                                        inputProps={{ style: { padding: "2px 6px", fontSize: 12 } }}
                                                    />
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>

                {/* Painel direito: catálogo */}
                <Box sx={{ width: "60%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            Cardápio
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: "auto" }}>
                        {arvore.map(categoria => (
                            <Accordion key={categoria.categoriaUuid} disableGutters TransitionProps={{ unmountOnExit: true }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "common.white" }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        {categoria.categoriaDescricao}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                    {categoria.produtos.map(produto => renderProduto(produto))}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2, borderTop: 1, borderColor: "divider", flexShrink: 0 }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={handleSalvar} disabled={loading}>
                    Salvar
                </Button>
            </Box>
        </Drawer>
    );
};
